import { z } from '@hono/zod-openapi'
import * as fileType from './file_type'
import * as tenantOccupantType from './tenant_occupant_type'

// id
export const idZ = z.number().openapi({ description: '入居申込ID', example: 1 })
export type id = z.infer<typeof idZ>

// roomId
export const roomIdZ = z
  .number()
  .openapi({ description: '申込対象の部屋ID', example: 201 })
export type roomId = z.infer<typeof roomIdZ>

// name: 氏名
export const nameZ = z
  .string()
  .openapi({ description: '申込者の氏名', example: '山田太郎' })
export type name = z.infer<typeof nameZ>

// name: 氏名カナ
export const nameKanaZ = z
  .string()
  .openapi({ description: '申込者の氏名カナ', example: 'ヤマダタロウ' })
export type nameKana = z.infer<typeof nameKanaZ>

// phone1: 電話番号1（市外局番）
export const phone1Z = z
  .string()
  .min(1)
  .max(4)
  .regex(/^\d{1,4}$/)
  .openapi({ description: '電話番号の市外局番', example: '03' })
export type phone1 = z.infer<typeof phone1Z>

// phone2: 電話番号2（市内局番）
export const phone2Z = z
  .string()
  .min(1)
  .max(4)
  .regex(/^\d{1,4}$/)
  .openapi({ description: '電話番号の市内局番', example: '1234' })
export type phone2 = z.infer<typeof phone2Z>

// phone3: 電話番号3（加入者番号）
export const phone3Z = z
  .string()
  .min(1)
  .max(4)
  .regex(/^\d{1,4}$/)
  .openapi({ description: '電話番号の加入者番号', example: '5678' })
export type phone3 = z.infer<typeof phone3Z>

// phone4: 電話番号2（市外局番）
export const phone4Z = z
  .string()
  .max(4)
  .regex(/^\d{0,4}$/)
  .openapi({ description: '電話番号2の市外局番', example: '03' })
export type phone4 = z.infer<typeof phone4Z>

// phone5: 電話番号2（市内局番）
export const phone5Z = z
  .string()
  .max(4)
  .regex(/^\d{0,4}$/)
  .openapi({ description: '電話番号2の市内局番', example: '1234' })
export type phone5 = z.infer<typeof phone5Z>

// phone6: 電話番号2（加入者番号）
export const phone6Z = z
  .string()
  .max(4)
  .regex(/^\d{0,4}$/)
  .openapi({ description: '電話番号2の加入者番号', example: '5678' })
export type phone6 = z.infer<typeof phone6Z>

// email: メールアドレス
export const emailZ = z.string().openapi({
  description: '申込者のメールアドレス',
  example: 'taro@example.com',
})
export type email = z.infer<typeof emailZ>

// birthday: 生年月日
export const birthdayZ = z
  .string()
  .regex(/^\d{4}-\d{2}-\d{2}$/)
  .nullable()
  .openapi({ description: '生年月日（YYYY-MM-DD）', example: '1990-01-01' })
export type birthday = z.infer<typeof birthdayZ>

// contractType: 契約区分
export const CONTRACT_TYPE_INDIVIDUAL = 'individual'
export const CONTRACT_TYPE_CORPORATION = 'corporation'
export const CONTRACT_TYPE_VALUES = [
  CONTRACT_TYPE_INDIVIDUAL,
  CONTRACT_TYPE_CORPORATION,
] as const
export const CONTRACT_TYPES = [
  { key: CONTRACT_TYPE_INDIVIDUAL, value: '個人' },
  { key: CONTRACT_TYPE_CORPORATION, value: '法人' },
] as const
export const contractTypeZ = z
  .enum([CONTRACT_TYPE_INDIVIDUAL, CONTRACT_TYPE_CORPORATION])
  .openapi({
    description: '契約区分（individual:個人 / corporation:法人）',
    example: CONTRACT_TYPE_INDIVIDUAL,
  })
export type contractType = z.infer<typeof contractTypeZ>

// startDate: 契約開始日
export const startDateZ = z
  .string()
  .regex(/^\d{4}-\d{2}-\d{2}$/)
  .openapi({
    description: '賃貸借契約の開始予定日（YYYY-MM-DD）',
    example: '2024-01-01',
  })
export type startDate = z.infer<typeof startDateZ>

// rent: 家賃
export const rentZ = z
  .number()
  .openapi({ description: '家賃（円・月額）', example: 80000 })
export type rent = z.infer<typeof rentZ>

// commonAreaFee: 共益費
export const commonAreaFeeZ = z
  .number()
  .openapi({ description: '共益費（円・月額）', example: 5000 })
export type commonAreaFee = z.infer<typeof commonAreaFeeZ>

// isParking: 駐車場の有無
export const isParkingZ = z
  .boolean()
  .openapi({ description: '駐車場契約の有無', example: true })
export type isParking = z.infer<typeof isParkingZ>

// parkingFee: 駐車場料金
export const parkingFeeZ = z
  .number()
  .openapi({ description: '駐車場料金（円・月額）', example: 10000 })
export type parkingFee = z.infer<typeof parkingFeeZ>

// parkingNumber: 駐車場番号
export const parkingNumberZ = z
  .string()
  .openapi({ description: '駐車場の区画番号', example: 'No.12' })
export type parkingNumber = z.infer<typeof parkingNumberZ>

// otherFees: その他費用
export const otherFeesZ = z
  .array(
    z.object({
      label: z.string(),
      price: z.number(),
      tax: z.number(),
    }),
  )
  .nullable()
  .openapi({
    description:
      'その他費用の内訳（label:名目 / price:税抜金額(円) / tax:消費税額(円)）',
    example: [
      { label: '水道代', price: 2727, tax: 273 },
      { label: 'インターネット代', price: 1818, tax: 182 },
    ],
  })
export type otherFees = z.infer<typeof otherFeesZ>

// occupants: 入居者
export const occupantsZ = tenantOccupantType.upsertOccupantsZ
export type occupants = z.infer<typeof occupantsZ>

// purpose: 用途
export const TENANT_APPLICATION_PURPOSE_LIVING = '1'
export const TENANT_APPLICATION_PURPOSE_BUSINESS = '2'
export const TENANT_APPLICATION_PURPOSES = [
  { key: TENANT_APPLICATION_PURPOSE_LIVING, value: '居住用' },
  { key: TENANT_APPLICATION_PURPOSE_BUSINESS, value: '事業用' },
] as const
export const purposeZ = z
  .enum([
    TENANT_APPLICATION_PURPOSE_LIVING,
    TENANT_APPLICATION_PURPOSE_BUSINESS,
  ])
  .openapi({
    description: '用途（1:居住用 / 2:事業用）',
    example: TENANT_APPLICATION_PURPOSE_LIVING,
  })
export type purpose = z.infer<typeof purposeZ>

// agencyName: 仲介会社名
export const agencyNameZ = z
  .string()
  .openapi({ description: '仲介会社名', example: '株式会社OOOO' })
export type agencyName = z.infer<typeof agencyNameZ>

// agencyPhone: 仲介会社 電話番号
export const agencyPhoneZ = z
  .string()
  .openapi({ description: '仲介会社の電話番号', example: '09012345678' })
export type agencyPhone = z.infer<typeof agencyPhoneZ>

// otherContact: その他連絡先
export const otherContactZ = z
  .string()
  .openapi({ description: 'その他の連絡先', example: 'tanaka@example.com' })
export type otherContact = z.infer<typeof otherContactZ>

// submittedAt: 送信完了日時
export const submittedAtStringZ = z.string().nullable().openapi({
  description: '申込内容が送信された日時（ISO8601）。null は未送信',
  example: '2024-01-01T00:00:00.000Z',
})
export type submittedAtString = z.infer<typeof submittedAtStringZ>

export const submittedAtDateZ = z
  .date()
  .nullable()
  .openapi({ example: '2024-01-01T00:00:00.000Z' })
export type submittedAtDate = z.infer<typeof submittedAtDateZ>

// approvalStatus: 承認状況
export const TENANT_APPLICATION_APPROVAL_STATUS_PENDING = 'pending'
export const TENANT_APPLICATION_APPROVAL_STATUS_APPROVED = 'approved'
export const TENANT_APPLICATION_APPROVAL_STATUS_REJECTED = 'rejected'
export const TENANT_APPLICATION_APPROVAL_STATUS_VALUES = [
  TENANT_APPLICATION_APPROVAL_STATUS_PENDING,
  TENANT_APPLICATION_APPROVAL_STATUS_APPROVED,
  TENANT_APPLICATION_APPROVAL_STATUS_REJECTED,
] as const
export const TENANT_APPLICATION_APPROVAL_STATUSES = [
  { key: TENANT_APPLICATION_APPROVAL_STATUS_PENDING, value: '未承認' },
  { key: TENANT_APPLICATION_APPROVAL_STATUS_APPROVED, value: '承認済み' },
  { key: TENANT_APPLICATION_APPROVAL_STATUS_REJECTED, value: '却下' },
] as const

export const approvalStatusZ = z
  .enum(TENANT_APPLICATION_APPROVAL_STATUS_VALUES)
  .openapi({
    description:
      'オーナーの承認状況（pending:未承認 / approved:承認済み / rejected:却下）',
    example: TENANT_APPLICATION_APPROVAL_STATUS_PENDING,
  })
export type approvalStatus = z.infer<typeof approvalStatusZ>

// ownerRespondedAt: オーナー回答日
export const ownerRespondedAtStringZ = z.string().nullable().openapi({
  description: 'オーナーが承認・却下を回答した日時（ISO8601）',
  example: '2024-01-01T00:00:00.000Z',
})
export type ownerRespondedAtString = z.infer<typeof ownerRespondedAtStringZ>

export const ownerRespondedAtDateZ = z
  .date()
  .nullable()
  .openapi({ example: '2024-01-01T00:00:00.000Z' })
export type ownerRespondedAtDate = z.infer<typeof ownerRespondedAtDateZ>

// createdAt: 作成日時
export const createdAtStringZ = z.string().openapi({
  description: '作成日時（ISO8601）',
  example: '2024-01-01T00:00:00.000Z',
})
export type createdAtString = z.infer<typeof createdAtStringZ>

export const createdAtDateZ = z
  .date()
  .openapi({ example: '2024-01-01T00:00:00.000Z' })
export type createdAtDate = z.infer<typeof createdAtDateZ>

// updatedAt: 更新日時
export const updatedAtStringZ = z.string().openapi({
  description: '更新日時（ISO8601）',
  example: '2024-01-01T00:00:00.000Z',
})
export type updatedAtString = z.infer<typeof updatedAtStringZ>

export const updatedAtDateZ = z
  .date()
  .openapi({ example: '2024-01-01T00:00:00.000Z' })
export type updatedAtDate = z.infer<typeof updatedAtDateZ>

// deletedAt: 削除日時
export const deletedAtStringZ = z
  .string()
  .nullable()
  .openapi({ description: '削除日時（ISO8601）。null は未削除', example: null })
export type deletedAtString = z.infer<typeof deletedAtStringZ>

export const deletedAtDateZ = z.date().nullable().openapi({ example: null })
export type deletedAtDate = z.infer<typeof deletedAtDateZ>

// ticketId: チケットID（承認フロー用）
export const ticketIdZ = z
  .number()
  .nullable()
  .openapi({ description: '承認フローで作成されたチケットID', example: 1 })
export type ticketId = z.infer<typeof ticketIdZ>

// rejectionReason: 棄却理由
export const rejectionReasonZ = z
  .string()
  .nullable()
  .openapi({ description: '却下理由', example: '契約内容に不備があるため' })
export type rejectionReason = z.infer<typeof rejectionReasonZ>

// needsFollowup: フォローアップ必要性
export const needsFollowupZ = z
  .boolean()
  .nullable()
  .openapi({ description: 'フォローアップが必要かどうか', example: true })
export type needsFollowup = z.infer<typeof needsFollowupZ>

// followupRequest: フォローアップ依頼内容
export const followupRequestZ = z.string().nullable().openapi({
  description: 'フォローアップの依頼内容',
  example: '契約書類の再提出をお願いします',
})
export type followupRequest = z.infer<typeof followupRequestZ>

// ファイル
export const uploadFilesZ = z
  .array(
    z.object({
      path: z.string().openapi({ example: 'tmp/uuid/timestamp' }),
      fileName: z.string().openapi({ example: 'contract.png' }),
      title: z
        .string()
        .openapi({ example: fileType.TENANT_FILE_TITLE_RENTAL_CONTRACT }),
      tenantFileType: fileType.tenantFileTypeZ,
    }),
  )
  .optional()
  .openapi({
    example: [
      {
        path: 'tmp/uuid/timestamp',
        fileName: 'contract.png',
        title: fileType.TENANT_FILE_TITLE_RENTAL_CONTRACT,
        tenantFileType: fileType.TENANT_FILE_TYPE_RENTAL_CONTRACT,
      },
    ],
  })
export type uploadFiles = z.infer<typeof uploadFilesZ>
